import type { ComponentType } from "react"

import type { BatteryProposalFinancingDto } from "~/api/dto/battery-proposal.dto"

import { LIGHTREACH_BATTERY_FINANCE_TYPE } from "../lightreach-battery"
import { SUNGAGE_BATTERY_FINANCE_TYPE } from "../sungage/sungage-battery-copy"
import { CONCERT_BATTERY_FINANCE_TYPE } from "./concert/concert-battery-copy"

/**
 * Finance-Partner-specific application status experience. Contract status stays
 * partner-agnostic; register here instead of branching inside the Contract drawer.
 */
export type BatteryFinancingStatusExperienceProps = {
  proposalId: string
  financing: BatteryProposalFinancingDto
}

export type BatteryFinancingStatusExperience =
  ComponentType<BatteryFinancingStatusExperienceProps>

/** Credit Human Battery Finance Partner Vision finance type. */
export const CREDIT_HUMAN_BATTERY_FINANCE_TYPE = `credit_human_battery`

const experiences = new Map<string, BatteryFinancingStatusExperience>()

export const registerBatteryFinancingStatusExperience = (
  financeType: string,
  experience: BatteryFinancingStatusExperience,
): void => {
  experiences.set(financeType, experience)
}

export const batteryFinancingStatusExperienceFor = (
  financeType: string | null | undefined,
): BatteryFinancingStatusExperience | null => {
  if (financeType == null || financeType.length === 0) {
    return null
  }
  return experiences.get(financeType) ?? null
}

export const financingHasRetainedLoanApplication = (
  financing: BatteryProposalFinancingDto,
): boolean =>
  financing.currentApplication != null ||
  financing.previousApplications.length > 0

/** Apply is the Credit Human entry before any Existing Loan Application. */
export const financingCanApplyForLoan = (
  financing: BatteryProposalFinancingDto,
): boolean =>
  !financingHasRetainedLoanApplication(financing) &&
  financing.actions.apply.allowed &&
  financing.quote?.id != null

/**
 * Reserve now only after financing succeeded with no retained application and
 * no Apply entry. A loading or failed financing read is not proof of either.
 */
export const canShowBatteryReserveNow = (
  financing: BatteryProposalFinancingDto | undefined,
): boolean =>
  financing != null &&
  !financingHasRetainedLoanApplication(financing) &&
  !financingCanApplyForLoan(financing)

const retainedApplicationProvider = (
  financing: BatteryProposalFinancingDto,
): string | undefined =>
  financing.currentApplication?.provider ??
  financing.previousApplications.find((application) => application.provider)
    ?.provider

const experienceForProvider = (
  provider: string | undefined,
): BatteryFinancingStatusExperience | null => {
  switch (provider) {
    case `sungage`:
      return batteryFinancingStatusExperienceFor(SUNGAGE_BATTERY_FINANCE_TYPE)
    case `concert`:
      return batteryFinancingStatusExperienceFor(CONCERT_BATTERY_FINANCE_TYPE)
    case `lightreach`:
      return batteryFinancingStatusExperienceFor(LIGHTREACH_BATTERY_FINANCE_TYPE)
    case `credit_human`:
      return batteryFinancingStatusExperienceFor(
        CREDIT_HUMAN_BATTERY_FINANCE_TYPE,
      )
    default:
      return null
  }
}

/**
 * Prefer the Existing Loan Application's provider so a Finance Plan change
 * (including Cash) still opens the matching drawer. Fall back to the current
 * plan, then to Credit Human for legacy rows that omit provider.
 */
export const resolveBatteryFinancingStatusExperience = (
  financing: BatteryProposalFinancingDto | null | undefined,
): BatteryFinancingStatusExperience | null => {
  if (financing == null) {
    return null
  }
  const byProvider = experienceForProvider(
    retainedApplicationProvider(financing),
  )
  if (byProvider != null) {
    return byProvider
  }
  const forPlan = batteryFinancingStatusExperienceFor(
    financing.financePlan?.financeType,
  )
  if (forPlan != null) {
    return forPlan
  }
  if (!financingHasRetainedLoanApplication(financing)) {
    return null
  }
  return batteryFinancingStatusExperienceFor(CREDIT_HUMAN_BATTERY_FINANCE_TYPE)
}

/** Test / hot-reload helper — production registers once at module load. */
export const clearBatteryFinancingStatusExperiences = (): void => {
  experiences.clear()
}
